import { queryClient } from '@shared/queries';
import { Stock, StockAllowance } from '../entity';
import { AddSaleItemStockAllowanceType } from './components/procedure-sales-form-dialog';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';

export type Procedure = {
  id: string;
  visitId: string;
  stepOrder: number;
  description: string | null;
  createdAt: Date;
  type: 'SALE' | 'CONSUMPTION';
  stockAllowanceId: string | null;
  stockAllowances: AddProcedureStockAllowanceType;
};
export type AddProcedureStockAllowanceType = (Omit<StockAllowance, 'id' | 'quantity'> & {
  stockAllowanceId: string;
  quantity: number | null;
  stockItemName?: string;
  costs: number;
  totalPrice: number;
  avgUnitPrice?: string;
})[];

export type StockAllowanceFormValues = {
  description: string;
  stockAllowances: AddProcedureStockAllowanceType;
};

export type AddProcedureButtonProps = {
  defaultValues?: {
    stockAllowances: AddProcedureStockAllowanceType;
    description: string;
  };
  procedureId?: string;
  hasNoProcedures: boolean;
  isVisitClosed: boolean;
  procedures: {
    id?: string;
    stepOrder: number;
    type: 'SALE' | 'CONSUMPTION';
    description?: string | null;
    stockAllowances: AddProcedureStockAllowanceType;
  }[];
  dialogRef?: React.RefObject<ImperativeFormDialogRefType | null>;
};
export type CreateProcedure = {
  id?: string;
  stepOrder: number;
  type: 'SALE' | 'CONSUMPTION';
  createdAt: Date;
  description?: string;
  visitId: string;
  stockAllowances: AddProcedureStockAllowanceType;
};

export type PostNewProcedure = {
  description: string;
  type: 'SALE' | 'CONSUMPTION';
  visitId: string;
  stockAllowances: {
    stockItemId: string;
    quantity: number;
    userId: string;
  }[];
};

export const mapDefaultStockAlowances = (
  defaultStockAllowances?: AddProcedureStockAllowanceType | AddSaleItemStockAllowanceType,
): {
  stockItemId: string;
  quantity: number | null;
  costs: number;
  stockAllowanceId: string;
  type: 'CONSUMPTION' | 'SALE';
  totalPrice: number;
}[] => {
  if (!defaultStockAllowances) {
    return [];
  }

  return defaultStockAllowances.map(stockAllowance => ({
    stockItemId: stockAllowance.stockItemId,
    quantity: Number(stockAllowance.quantity),
    stockAllowanceId: stockAllowance.stockAllowanceId ?? '',
    costs: Number(stockAllowance.costs),
    totalPrice: Number(stockAllowance.avgUnitPrice) * Number(stockAllowance.quantity),
    avgUnitPrice: Number(stockAllowance.avgUnitPrice),
    type: stockAllowance.type,
  }));
};

export const getProcedureInvalidation = (
  stocks: Stock[] | undefined,
  visitId: string | undefined,
  clientId: string | undefined,
) => {
  queryClient.invalidateQueries({ queryKey: ['stockItems'] });
  queryClient.invalidateQueries({ queryKey: [clientId, 'visits'] });
  queryClient.invalidateQueries({ queryKey: ['procedures', visitId] });
  queryClient.invalidateQueries({ queryKey: ['visit', visitId] });
  queryClient.invalidateQueries({ queryKey: ['stockItems', stocks && stocks[0].id] });
};

export const PROCEDURE_TYPE = {
  SALE: 'SALE',
  CONSUMPTION: 'CONSUMPTION',
  PREVIOUS_COPY: 'PREVIOUS_COPY',
} as const;

export type ProcedureType = (typeof PROCEDURE_TYPE)[keyof typeof PROCEDURE_TYPE];

export type ProcedureTypeBasic = Exclude<ProcedureType, 'PREVIOUS_COPY'>;
